import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { fireConfetti } from '@/lib/perf/confetti';
import { safeError } from '@/lib/safeLog';

export type ProfileStepId = 
  | 'core' 
  | 'shopping' 
  | 'entertainment' 
  | 'travel' 
  | 'food' 
  | 'financial' 
  | 'technology' 
  | 'brand';

export interface ProfileStepOption {
  id: string;
  label: string;
  description?: string;
}

export interface ProfileStepField {
  key: string;
  question: string;
  type: 'single' | 'multi' | 'text';
  options?: ProfileStepOption[];
  placeholder?: string;
}

export interface ProfileStep {
  id: ProfileStepId;
  title: string;
  subtitle: string;
  icon: string;
  entriesReward: number;
  fields: ProfileStepField[];
}

export const PROFILE_STEPS: ProfileStep[] = [
  {
    id: 'core',
    title: 'About You',
    subtitle: 'Basic info helps brands send relevant offers.',
    icon: 'User',
    entriesReward: 10,
    fields: [
      {
        key: 'first_name',
        question: 'What\'s your first name?',
        type: 'text',
        placeholder: 'Enter your first name',
      },
      {
        key: 'last_name',
        question: 'What\'s your last name?',
        type: 'text',
        placeholder: 'Enter your last name',
      },
      {
        key: 'core_age_range',
        question: 'What\'s your age range?',
        type: 'single',
        options: [
          { id: '18-24', label: '18-24' },
          { id: '25-34', label: '25-34' },
          { id: '35-44', label: '35-44' },
          { id: '45-54', label: '45-54' },
          { id: '55-64', label: '55-64' },
          { id: '65+', label: '65+' },
        ],
      },
      {
        key: 'core_household',
        question: 'How many people live in your household?',
        type: 'single',
        options: [
          { id: '1', label: 'Just me' },
          { id: '2', label: '2 people' },
          { id: '3-4', label: '3-4 people' },
          { id: '5+', label: '5+ people' },
        ],
      },
      {
        key: 'core_education',
        question: 'What\'s your highest education level?',
        type: 'single',
        options: [
          { id: 'high_school', label: 'High school' },
          { id: 'some_college', label: 'Some college' },
          { id: 'bachelors', label: 'Bachelor\'s degree' },
          { id: 'masters_plus', label: 'Master\'s or higher' },
        ],
      },
    ],
  },
  {
    id: 'shopping',
    title: 'Shopping Habits',
    subtitle: 'Brands pay well to reach active shoppers.',
    icon: 'ShoppingBag',
    entriesReward: 15,
    fields: [
      {
        key: 'shopping_frequency',
        question: 'How often do you shop online?',
        type: 'single',
        options: [
          { id: 'weekly', label: 'Weekly or more' },
          { id: '2-3_monthly', label: '2-3 times per month' },
          { id: 'monthly', label: 'About once a month' },
          { id: 'rarely', label: 'Rarely' },
        ],
      },
      {
        key: 'shopping_style',
        question: 'What\'s your shopping style?',
        type: 'single',
        options: [
          { id: 'deal_hunter', label: 'Deal hunter - always looking for discounts' },
          { id: 'brand_loyal', label: 'Brand loyal - stick with favorites' },
          { id: 'quality_first', label: 'Quality first - willing to pay more' },
          { id: 'impulse', label: 'Impulse buyer - buy what catches my eye' },
        ],
      },
      {
        key: 'shopping_brands',
        question: 'Which retailers do you shop at? (Select all)',
        type: 'multi',
        options: [
          { id: 'amazon', label: 'Amazon' },
          { id: 'target', label: 'Target' },
          { id: 'walmart', label: 'Walmart' },
          { id: 'costco', label: 'Costco' },
          { id: 'nike', label: 'Nike' },
          { id: 'apple', label: 'Apple' },
        ],
      },
    ],
  },
  {
    id: 'entertainment',
    title: 'Entertainment',
    subtitle: 'Streaming and media preferences unlock premium surveys.',
    icon: 'Tv',
    entriesReward: 12,
    fields: [
      {
        key: 'streaming_services',
        question: 'Which streaming services do you use? (Select all)',
        type: 'multi',
        options: [
          { id: 'netflix', label: 'Netflix' },
          { id: 'spotify', label: 'Spotify' },
          { id: 'disney', label: 'Disney+' },
          { id: 'hbo', label: 'HBO Max' },
          { id: 'youtube_premium', label: 'YouTube Premium' },
          { id: 'apple_music', label: 'Apple Music' },
        ],
      },
      {
        key: 'podcast_interests',
        question: 'What podcast topics interest you? (Select all)',
        type: 'multi',
        options: [
          { id: 'business', label: 'Business' },
          { id: 'tech', label: 'Tech' },
          { id: 'wellness', label: 'Wellness' },
          { id: 'true_crime', label: 'True Crime' },
          { id: 'comedy', label: 'Comedy' },
          { id: 'news', label: 'News & Politics' },
        ],
      },
    ],
  },
  {
    id: 'travel',
    title: 'Travel Habits',
    subtitle: 'Travel brands pay top dollar for traveler insights.',
    icon: 'Plane',
    entriesReward: 15,
    fields: [
      {
        key: 'travel_frequency',
        question: 'How often do you travel for leisure?',
        type: 'single',
        options: [
          { id: '4+_yearly', label: '4+ trips per year' },
          { id: '2-3_yearly', label: '2-3 trips per year' },
          { id: '1_yearly', label: 'About once a year' },
          { id: 'rarely', label: 'Rarely travel' },
        ],
      },
      {
        key: 'travel_preference',
        question: 'What\'s your travel preference?',
        type: 'single',
        options: [
          { id: 'domestic', label: 'Domestic preferred' },
          { id: 'international', label: 'International preferred' },
          { id: 'both', label: 'Mix of both' },
        ],
      },
      {
        key: 'travel_style',
        question: 'What\'s your travel style?',
        type: 'single',
        options: [
          { id: 'budget', label: 'Budget-conscious' },
          { id: 'mid_range', label: 'Mid-range comfort' },
          { id: 'luxury', label: 'Luxury experiences' },
          { id: 'adventure', label: 'Adventure travel' },
        ],
      },
    ],
  },
  {
    id: 'food',
    title: 'Food & Grocery',
    subtitle: 'Food brands love these insights.',
    icon: 'UtensilsCrossed',
    entriesReward: 10,
    fields: [
      {
        key: 'food_preference',
        question: 'What\'s your food preference?',
        type: 'single',
        options: [
          { id: 'organic', label: 'Mostly organic' },
          { id: 'mixed', label: 'Mix of organic and conventional' },
          { id: 'conventional', label: 'Mostly conventional' },
          { id: 'specialty', label: 'Specialty diets (vegan, gluten-free, etc.)' },
        ],
      },
      {
        key: 'grocery_stores',
        question: 'Where do you usually shop for groceries? (Select all)',
        type: 'multi',
        options: [
          { id: 'whole_foods', label: 'Whole Foods' },
          { id: 'trader_joes', label: 'Trader Joe\'s' },
          { id: 'kroger', label: 'Kroger / Ralph\'s' },
          { id: 'costco', label: 'Costco' },
          { id: 'walmart', label: 'Walmart' },
          { id: 'aldi', label: 'Aldi' },
        ],
      },
    ],
  },
  {
    id: 'financial',
    title: 'Financial Signals',
    subtitle: 'Non-sensitive, high-value brand data.',
    icon: 'DollarSign',
    entriesReward: 20,
    fields: [
      {
        key: 'income_range',
        question: 'What\'s your household income range?',
        type: 'single',
        options: [
          { id: 'under_50k', label: 'Under $50k' },
          { id: '50k-75k', label: '$50k - $75k' },
          { id: '75k-100k', label: '$75k - $100k' },
          { id: '100k-150k', label: '$100k - $150k' },
          { id: '150k+', label: '$150k+' },
        ],
      },
      {
        key: 'spending_style',
        question: 'How would you describe your spending style?',
        type: 'single',
        options: [
          { id: 'value_focused', label: 'Value-focused' },
          { id: 'balanced', label: 'Balanced - mix of saving and spending' },
          { id: 'quality_driven', label: 'Quality-driven - invest in what matters' },
          { id: 'luxury', label: 'Luxury-oriented' },
        ],
      },
      {
        key: 'payment_preferences',
        question: 'How do you prefer to pay? (Select all)',
        type: 'multi',
        options: [
          { id: 'credit_card', label: 'Credit card' },
          { id: 'debit_card', label: 'Debit card' },
          { id: 'digital_wallet', label: 'Digital wallet (Apple Pay, etc.)' },
          { id: 'bnpl', label: 'Buy now, pay later' },
          { id: 'cash', label: 'Cash' },
        ],
      },
    ],
  },
  {
    id: 'technology',
    title: 'Technology',
    subtitle: 'Tech brands value these preferences.',
    icon: 'Smartphone',
    entriesReward: 12,
    fields: [
      {
        key: 'primary_device',
        question: 'What\'s your primary phone?',
        type: 'single',
        options: [
          { id: 'iphone', label: 'iPhone' },
          { id: 'android', label: 'Android' },
        ],
      },
      {
        key: 'secondary_device',
        question: 'What computer do you primarily use?',
        type: 'single',
        options: [
          { id: 'mac', label: 'Mac' },
          { id: 'windows', label: 'Windows PC' },
          { id: 'chromebook', label: 'Chromebook' },
          { id: 'none', label: 'Primarily mobile' },
        ],
      },
      {
        key: 'privacy_sensitivity',
        question: 'How privacy-conscious are you?',
        type: 'single',
        options: [
          { id: 'very_high', label: 'Very high - careful about all data sharing' },
          { id: 'moderate', label: 'Moderate - selective sharing' },
          { id: 'low', label: 'Low - convenience over privacy' },
        ],
      },
    ],
  },
  {
    id: 'brand',
    title: 'Brand Interests',
    subtitle: 'This is gold for brands — and more rewards for you.',
    icon: 'Target',
    entriesReward: 15,
    fields: [
      {
        key: 'interested_categories',
        question: 'Which categories interest you? (Select all)',
        type: 'multi',
        options: [
          { id: 'fitness', label: 'Fitness & wellness' },
          { id: 'finance', label: 'Personal finance tools' },
          { id: 'travel', label: 'Travel deals' },
          { id: 'tech', label: 'Consumer tech' },
          { id: 'fashion', label: 'Fashion & beauty' },
          { id: 'home', label: 'Home & garden' },
        ],
      },
      {
        key: 'low_interest_categories',
        question: 'Categories you\'re NOT interested in? (Select all)',
        type: 'multi',
        options: [
          { id: 'luxury', label: 'Luxury goods' },
          { id: 'gambling', label: 'Gambling' },
          { id: 'high_risk_finance', label: 'High-risk financial products' },
          { id: 'alcohol', label: 'Alcohol' },
          { id: 'tobacco', label: 'Tobacco' },
        ],
      },
    ],
  },
];

interface StepData {
  [key: string]: string | string[];
}

interface ProfileWizardContextType {
  isOpen: boolean;
  currentStepIndex: number;
  currentFieldIndex: number;
  currentStep: ProfileStep | null;
  currentField: ProfileStepField | null;
  selectedValues: StepData;
  isLoading: boolean;
  isSaving: boolean;
  completedSteps: ProfileStepId[];
  totalSteps: number;
  progress: number;
  totalPotentialEntries: number;
  earnedEntries: number;
  remainingEntries: number;
  openWizard: () => void;
  closeWizard: () => void;
  selectOption: (optionId: string) => void;
  setTextValue: (value: string) => void;
  continueToNext: () => Promise<void>;
  goBack: () => void;
  canContinue: boolean;
  canGoBack: boolean;
  resetProfile: () => Promise<void>;
}

const ProfileWizardContext = createContext<ProfileWizardContextType | null>(null);

export function ProfileWizardProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [selectedValues, setSelectedValues] = useState<StepData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<ProfileStepId[]>([]);

  const currentStep = PROFILE_STEPS[currentStepIndex] || null;
  const currentField = currentStep?.fields[currentFieldIndex] || null;

  // Load existing profile data
  useEffect(() => {
    async function loadExistingData() {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_knowledge')
          .select('attribute_key, attribute_value')
          .eq('user_id', user.id)
          .eq('source', 'lemonade_wizard');

        if (error) throw error;

        const existingData: StepData = {};
        const completed: ProfileStepId[] = [];

        data?.forEach((item) => {
          const value = item.attribute_value as { selection?: string | string[] };
          if (value.selection) {
            existingData[item.attribute_key] = value.selection;
          }
        });

        PROFILE_STEPS.forEach((step) => {
          const stepComplete = step.fields.every((field) => {
            const val = existingData[field.key];
            return val && (Array.isArray(val) ? val.length > 0 : true);
          });
          if (stepComplete) {
            completed.push(step.id);
          }
        });

        setSelectedValues(existingData);
        setCompletedSteps(completed);

        const firstIncompleteIndex = PROFILE_STEPS.findIndex(
          (step) => !completed.includes(step.id)
        );
        if (firstIncompleteIndex >= 0) {
          setCurrentStepIndex(firstIncompleteIndex);
          
          // Find first unanswered field within this step
          const incompleteStep = PROFILE_STEPS[firstIncompleteIndex];
          const firstUnansweredFieldIndex = incompleteStep.fields.findIndex((field) => {
            const val = existingData[field.key];
            return !val || (Array.isArray(val) && val.length === 0);
          });
          
          if (firstUnansweredFieldIndex >= 0) {
            setCurrentFieldIndex(firstUnansweredFieldIndex);
          }
        }
      } catch (err) {
        safeError('Failed to load profile data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadExistingData();
  }, [user?.id]);

  const selectOption = useCallback((optionId: string) => {
    if (!currentField) return;

    setSelectedValues((prev) => {
      const currentValue = prev[currentField.key];

      if (currentField.type === 'single') {
        return { ...prev, [currentField.key]: optionId };
      } else if (currentField.type === 'multi') {
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        const newArray = currentArray.includes(optionId)
          ? currentArray.filter((id) => id !== optionId)
          : [...currentArray, optionId];
        return { ...prev, [currentField.key]: newArray };
      }
      return prev;
    });
  }, [currentField]);

  const setTextValue = useCallback((value: string) => {
    if (!currentField || currentField.type !== 'text') return;
    setSelectedValues((prev) => ({ ...prev, [currentField.key]: value }));
  }, [currentField]);

  const saveFieldToDb = useCallback(async (key: string, value: string | string[]) => {
    if (!user?.id) return;

    const { error } = await supabase
      .from('user_knowledge')
      .upsert({
        user_id: user.id,
        attribute_key: key,
        attribute_value: { selection: value },
        source: 'lemonade_wizard',
        confidence_score: 1.0,
        is_user_verified: true,
        collected_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,attribute_key',
      });

    if (error) {
      console.error('Failed to save profile data:', error);
      throw error;
    }
  }, [user?.id]);

  const continueToNext = useCallback(async () => {
    if (!currentStep || !currentField || !user?.id) return;

    const currentValue = selectedValues[currentField.key];
    if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
      return;
    }

    setIsSaving(true);
    try {
      await saveFieldToDb(currentField.key, currentValue);

      if (currentFieldIndex < currentStep.fields.length - 1) {
        setCurrentFieldIndex(currentFieldIndex + 1);
      } else {
        // Step completed - award points and entries if not already completed
        if (!completedSteps.includes(currentStep.id)) {
          setCompletedSteps((prev) => [...prev, currentStep.id]);
          
          // Celebrate step completion with confetti!
          fireConfetti({
            particleCount: 80,
            spread: 60,
          });
          
          // Get next FUTURE draws for jackpot entries (weekly and monthly)
          const { data: weeklyDraw } = await supabase
            .from('jackpot_draws')
            .select('id')
            .eq('draw_type', 'weekly')
            .eq('is_drawn', false)
            .gt('scheduled_at', new Date().toISOString())
            .order('scheduled_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          const { data: monthlyDraw } = await supabase
            .from('jackpot_draws')
            .select('id')
            .eq('draw_type', 'monthly')
            .eq('is_drawn', false)
            .gt('scheduled_at', new Date().toISOString())
            .order('scheduled_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          // Award jackpot entries to weekly draw
          if (weeklyDraw?.id) {
            await supabase
              .from('jackpot_entries')
              .insert({
                user_id: user.id,
                draw_id: weeklyDraw.id,
                entry_count: currentStep.entriesReward,
                source: 'profile_completion',
              });
          }

          // Award jackpot entries to monthly draw
          if (monthlyDraw?.id) {
            await supabase
              .from('jackpot_entries')
              .insert({
                user_id: user.id,
                draw_id: monthlyDraw.id,
                entry_count: currentStep.entriesReward,
                source: 'profile_completion',
              });
          }

          // Award points securely via edge function
          try {
            await supabase.functions.invoke('award-points', {
              body: {
                amount: currentStep.entriesReward,
                transactionType: 'profile',
                description: `Completed profile step: ${currentStep.title}`,
              },
            });
            
            // Invalidate wallet and related queries to refresh UI immediately
            queryClient.invalidateQueries({ queryKey: ['wallet'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-bundle'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
            queryClient.invalidateQueries({ queryKey: ['profile'] });
            queryClient.invalidateQueries({ queryKey: ['point-transactions'] });
          } catch (pointsError) {
            safeError('Failed to award profile points:', pointsError);
            // Don't block profile completion if points fail
          }
        }

        if (currentStepIndex < PROFILE_STEPS.length - 1) {
          setCurrentStepIndex(currentStepIndex + 1);
          setCurrentFieldIndex(0);
        } else {
          setIsOpen(false);
        }
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  }, [currentStep, currentField, currentFieldIndex, currentStepIndex, selectedValues, completedSteps, saveFieldToDb, user?.id]);

  const goBack = useCallback(() => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex(currentFieldIndex - 1);
    } else if (currentStepIndex > 0) {
      const prevStep = PROFILE_STEPS[currentStepIndex - 1];
      setCurrentStepIndex(currentStepIndex - 1);
      setCurrentFieldIndex(prevStep.fields.length - 1);
    }
  }, [currentFieldIndex, currentStepIndex]);

  const canContinue = currentField
    ? (() => {
        const val = selectedValues[currentField.key];
        if (!val) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim().length > 0;
        return true;
      })()
    : false;

  const canGoBack = currentStepIndex > 0 || currentFieldIndex > 0;

  const totalFields = PROFILE_STEPS.reduce((acc, step) => acc + step.fields.length, 0);
  const completedFields = completedSteps.reduce((acc, stepId) => {
    const step = PROFILE_STEPS.find(s => s.id === stepId);
    return acc + (step?.fields.length || 0);
  }, 0);
  const progress = Math.round((completedFields / totalFields) * 100);

  const totalPotentialEntries = PROFILE_STEPS.reduce((sum, step) => sum + step.entriesReward, 0);
  const earnedEntries = completedSteps.reduce((sum, stepId) => {
    const step = PROFILE_STEPS.find(s => s.id === stepId);
    return sum + (step?.entriesReward || 0);
  }, 0);
  const remainingEntries = totalPotentialEntries - earnedEntries;

  const resetProfile = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      await supabase
        .from('user_knowledge')
        .delete()
        .eq('user_id', user.id)
        .eq('source', 'lemonade_wizard');
      
      setSelectedValues({});
      setCompletedSteps([]);
      setCurrentStepIndex(0);
      setCurrentFieldIndex(0);
    } catch (err) {
      safeError('Failed to reset profile:', err);
    }
  }, [user?.id]);

  const value: ProfileWizardContextType = {
    isOpen,
    currentStepIndex,
    currentFieldIndex,
    currentStep,
    currentField,
    selectedValues,
    isLoading,
    isSaving,
    completedSteps,
    totalSteps: PROFILE_STEPS.length,
    progress,
    totalPotentialEntries,
    earnedEntries,
    remainingEntries,
    openWizard: () => setIsOpen(true),
    closeWizard: () => setIsOpen(false),
    selectOption,
    setTextValue,
    continueToNext,
    goBack,
    canContinue,
    canGoBack,
    resetProfile,
  };

  return (
    <ProfileWizardContext.Provider value={value}>
      {children}
    </ProfileWizardContext.Provider>
  );
}

export function useProfileWizard(): ProfileWizardContextType {
  const context = useContext(ProfileWizardContext);
  if (!context) {
    throw new Error('useProfileWizard must be used within a ProfileWizardProvider');
  }
  return context;
}
